import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gatorProtectActions } from '@/redux/modules/gatorProtect';
import {
  ActivitiesChart,
  PlansBanner,
  PROTECTION_CARD_TYPES,
  Summary,
} from './components';
import * as Styles from './GatorProtectHandler.styles';

import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import ProtectionModal from './components/ProtectionModal';
import { productsActions } from '@/redux/modules/products';
import { GATOR_PROTECT_ID } from './GatorProtect.enum';
import { parseGatorProtectUpgradeHostingId } from '@/redux/modules/gatorProtect/gatorProtect.buy.utils';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { useHistory } from 'react-router';

const getAreDaysExpired = (selectedPlan) => {
  if (!selectedPlan) {
    return false;
  }

  if (selectedPlan.expire_days != null) {
    return Number.isInteger(selectedPlan.expire_days) && selectedPlan.expire_days <= 0;
  }
  return false;
};

const getFormattedStartDate = () => {
  const last30Days = new Date();
  last30Days.setDate(last30Days.getDate() - 30);
  return last30Days.toISOString().slice(0, 10);
};


const getSelectedPlanInfo = (selectedPlan) => {
  const areDaysExpired = getAreDaysExpired(selectedPlan);
  const isTrialActive = selectedPlan ? selectedPlan.is_trial && !areDaysExpired : false;
  const isTrialExpired = selectedPlan ? areDaysExpired : false;
  const isAAClient = selectedPlan?.trial_days === 365;
  const hasClientUsedTrial = selectedPlan?.trial_used;

  return {
    areDaysExpired,
    isTrialActive,
    isTrialExpired,
    isProActive: selectedPlan ? selectedPlan.is_pro : false,
    isAAClient,
    hasClientUsedTrial,
    protectionCardType: (isTrialActive || isTrialExpired)
      ? PROTECTION_CARD_TYPES.TRIAL_IS_ACTIVE
      : PROTECTION_CARD_TYPES.AUTOMATIC_PROTECTION_AVAILABLE,
  };
};

const GatorProtectHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    gatorProtect: gatorProtectLocale,
    routes: routesLocale,
  } = useLocale();
  const { loading, plans, planDetails } = useSelector(state => state.gatorProtect);
  const { loading: fastCheckoutLoading } = useSelector(state => state.fastCheckout);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [protectionModal, setProtectionModal] = useState({
    open: false,
    activationKey: null,
    agreed: false,
  });
  const { consultedProducts } = useSelector(state => state.products);
  const gatorProtectProData = consultedProducts?.find(item => item.id === GATOR_PROTECT_ID.PRO);

  const isTurbo = useMemo(() => selectedPlan?.shared_hosting_plan?.toLowerCase().includes('turbo'), [selectedPlan]);

  const {
    isTrialActive,
    isTrialExpired,
    isProActive,
    isAAClient,
    protectionCardType,
    hasClientUsedTrial,
  } = getSelectedPlanInfo(selectedPlan);

  useEffect(() => {
    // Get the pro price for the current server
    dispatch(productsActions.products.getProductById(GATOR_PROTECT_ID.PRO));
  }, [dispatch]);

  useEffect(() => {
    // Get the plans for the current server
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  const handleOnChangePlan = (plan) => {
    setSelectedPlan(plan);
    const formattedStartDate = getFormattedStartDate();
    dispatch(gatorProtectActions.planDetails.request({
      hostingId: plan.shared_hosting_id,
      startDate: formattedStartDate,
    }));
  };

  const closeProtectionModal = () => {
    setProtectionModal({ open: false, activationKey: null, agreed: false });
  };

  const openProtectionProModal = () => {
    setProtectionModal({ open: true, activationKey: 'activateProtectionPro', agreed: false });
  };

  const openProtectionTrialModal = () => {
    setProtectionModal({ open: true, activationKey: 'activateTrial', agreed: false });
  };

  const handleToggleAgreed = () => {
    setProtectionModal(prev => ({ ...prev, agreed: !prev.agreed }));
  };

  const handleTermsAcceptance = () => {
    const hostingId = selectedPlan?.shared_hosting_id;
    if (hostingId == null || !protectionModal.agreed) {
      return;
    }
    dispatch(gatorProtectActions.acceptTerms.request({
      hostingId,
      termsAccepted: protectionModal.agreed ? 'TrialActivate' : null,
    }));
  };

  const protectionModalContent = useMemo(() => {
    const modalContent = gatorProtectLocale
      && gatorProtectLocale.summary
      && gatorProtectLocale.summary.protectionModal;

    if (!modalContent) {
      return null;
    }

    const getContent = () => {
      const renderNewProtectionContent = !isTrialExpired && protectionModal?.activationKey === 'activateProtectionPro';

      if (renderNewProtectionContent) {
        return {
          ...modalContent.proActivation,
          buttonLabel:
          typeof modalContent?.proActivation?.buttonLabel === 'function'
            ? modalContent.proActivation.buttonLabel(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...')
            : modalContent.proActivation.buttonLabel
          ,
        };
      }

      if (isTrialActive) {
        return {
          ...modalContent.trialActive,
          buttonLabel:
            typeof modalContent?.trialActive?.buttonLabel === 'function'
              ? modalContent.trialActive.buttonLabel(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...')
              : modalContent.trialActive.buttonLabel,
        };
      }

      if (isTrialExpired) {
        return modalContent.trialActive;
      }

      return {
        ...modalContent.trialActivation,
        title: modalContent.trialActivation.title(isAAClient),
        subtitle: modalContent.trialActivation.subtitle(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...'),
      };
    };

    const scenario = getContent();
    const parsedMessage = typeof scenario.message === 'function'
      ? scenario.message((selectedPlan && selectedPlan.expire_days) || 0)
      : scenario.message;

    const commonContent = modalContent.common || {};

    return {
      ...scenario,
      message: parsedMessage,
      checklistItems: scenario.checklistItems || commonContent.checklistItems || [],
      productTitle: commonContent.productTitle || gatorProtectLocale.banner.title,
      productSubtitle: commonContent.productSubtitle || gatorProtectLocale.banner.description,
      proBadgeLabel: commonContent.proBadgeLabel || gatorProtectLocale.banner.proBadgeLabel,
      mascotAlt: gatorProtectLocale.banner.img.alt,
    };
  }, [gatorProtectLocale, isTrialActive, selectedPlan, isTrialExpired, gatorProtectProData, isAAClient, protectionModal]);

  const handleAutomaticCleaningToggle = () => {
    const current = planDetails.auto_protect ?? true;
    const next = !current;
    dispatch(gatorProtectActions.planDetails.setAutoProtect(next));
    if (selectedPlan?.shared_hosting_id != null) {
      dispatch(gatorProtectActions.planDetails.updateAutoProtect.request({
        hostingId: selectedPlan.shared_hosting_id,
        autoProtect: next,
      }));
    }
  };

  useEffect(() => {
    if (plans.length > 0 && selectedPlan === null) {
      const plan = plans[0];
      setSelectedPlan(plan);
      const formattedStartDate = getFormattedStartDate();
      dispatch(gatorProtectActions.planDetails.request({
        hostingId: plan.shared_hosting_id,
        startDate: formattedStartDate,
      }));
    }
  }, [plans, selectedPlan, dispatch]);

  const handleActivate = (activationKey) => {
    const redirectToInvoice = (invoiceId) => {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}`);
    };

    switch (activationKey) {
      case 'activateTrial': {
        dispatch(gatorProtectActions.purchase.fastCheckout.request({
          productId: GATOR_PROTECT_ID.TRIAL,
          domain: selectedPlan?.shared_hosting_domain,
          redirectToInvoice: false,
          onPurchaseSuccess: () => {
            setSelectedPlan({
              ...selectedPlan,
              is_trial: true,
              expire_days: isAAClient ? 365 : 30,
            });
            setProtectionModal({
              open: false,
              activationKey: null,
              agreed: false,
            });
            dispatch(gatorProtectActions.plans.request({ headerId: null }));
            const formattedStartDate = getFormattedStartDate();
            dispatch(gatorProtectActions.planDetails.request({
              hostingId: selectedPlan.shared_hosting_id,
              startDate: formattedStartDate,
            }));
          },
        }));
        break;
      }
      case 'activateProtectionPro': {
        const upgradeHostingId = parseGatorProtectUpgradeHostingId(selectedPlan?.gatorprotect_hosting_id);
        if (upgradeHostingId != null) {
          dispatch(gatorProtectActions.purchase.upgradeOrder.request({
            hostingId: upgradeHostingId,
            newProductId: GATOR_PROTECT_ID.PRO,
            newBillingCycle: 'monthly',
            redirectToInvoice,
          }));
        } else {
          dispatch(gatorProtectActions.purchase.fastCheckout.request({
            productId: GATOR_PROTECT_ID.PRO,
            domain: selectedPlan?.shared_hosting_domain,
            redirectToInvoice,
          }));
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <Styles.Container data-testid="gator-protect-handler">
      <PlansBanner
        content={gatorProtectLocale.banner}
        plans={plans}
        loading={loading || plans.length === 0}
        selectedPlan={selectedPlan}
        onChangePlan={handleOnChangePlan}
      />
      <Summary content={gatorProtectLocale.summary} loading={loading}>
        {({ Banners, Main, Alerts }) => (
          <>
            <Banners>
              {isTrialActive && <Summary.Banner type="trialActive" trialDaysLeft={(selectedPlan && selectedPlan.expire_days) || 0} />}
              {isProActive && <Summary.Banner type="fullProtectionActive" />}
              {isTrialExpired && <Summary.Banner type="expiredProtection" />}
            </Banners>
            <Main>
              <Summary.Title
                loading={loading}
                enabled={isProActive ? (planDetails.auto_protect ?? true) : undefined}
                onToggle={isProActive ? handleAutomaticCleaningToggle : undefined}
              />
              <Styles.DataAndMetricsRow autoColumns={isProActive}>
                <Summary.Metrics
                  maliciousCodesRemoved={planDetails.summary.malicious}
                  compromisedFiles={planDetails.summary.compromised}
                />

                {!isProActive && loading && <Summary.ProtectionCardSkeleton />}
                {!isProActive && !loading && (
                  <Summary.ProtectionCard
                    type={protectionCardType}
                    onActivateProtectionPro={openProtectionProModal}
                    onActivateTrial={openProtectionTrialModal}
                    isAAClient={isAAClient}
                    hideActivateTrial={isTrialActive || hasClientUsedTrial}
                    isTurbo={isTurbo}
                  />
                )}
              </Styles.DataAndMetricsRow>
            </Main>
            <Alerts />
          </>
        )}
      </Summary>
      <ActivitiesChart
        content={gatorProtectLocale.activitiesChart}
        data={[...(planDetails.activity_detected || [])].sort((a, b) => new Date(a.date) - new Date(b.date))}
        loading={loading}
      />
      {(protectionModal.open && protectionModalContent) && (
        <ProtectionModal
          content={protectionModalContent}
          agreed={protectionModal.agreed}
          onToggleAgreed={handleToggleAgreed}
          onButtonClick={() => {
            handleTermsAcceptance();
            handleActivate(protectionModal.activationKey);
          }}
          onClose={closeProtectionModal}
          terms={gatorProtectLocale.terms}
          loading={loading || !consultedProducts?.length}
          fastCheckoutLoading={fastCheckoutLoading}
        />
      )}
    </Styles.Container>
  );
};

export default withErrorBoundary(GatorProtectHandler, 'GatorProtectHandler');
